// WsStudent.js
// Maneja el WebSocket del Ztudiante.
// - Conecta solo una vez (singleton simple).
// - Construye URL ws/wss dinámicamente.
// - Escucha 'request-snapshot' y responde con 'snapshot' capturando el viewport.
// - Incluye ping cada 25s para mantener viva la conexión.
//
// Requiere: npm i html2canvas

import html2canvas from 'html2canvas'

let socket = null
let isConnected = false
let reconnectTimer = null
let reconnectAttempts = 0
const MAX_RECONNECT_ATTEMPTS = 8
const PING_INTERVAL_MS = 25_000
let pingTimer = null

// Cache de identidad actual del estudiante (para reconexiones)
let currentIdentity = null // { centerId, username, name, lastName }

// ==== Utilidades ============================================================

function buildWsUrl({ centerId, username, name, lastName }) {
  //const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws'
  const scheme = 'wss'
  const host = (process.env.REACT_APP_WS_HOST ?? window.location.host) // ej: localhost:5000
  const base = `${scheme}://${host}/ws`

  const params = new URLSearchParams()
  params.set('centerId', String(centerId))
  params.set('role', 'student')
  params.set('user', username)
  if (name) params.set('name', name)
  if (lastName) params.set('surname', lastName)

  return `${base}?${params.toString()}`
}

// Captura del viewport como PNG DataURL
async function captureViewportDataUrl() {
  try {
    const canvas = await html2canvas(document.body, {
      useCORS: true,
      logging: false,
      // Tamaño del viewport actual
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      backgroundColor: null
    })
    return canvas.toDataURL('image/png', 0.92)
  } catch (err) {
    console.warn('[WS Student] No fue posible capturar viewport:', err)
    return null
  }
}

function safeSend(obj) {
  try {
    if (!socket || socket.readyState !== WebSocket.OPEN) return false
    socket.send(JSON.stringify(obj))
    return true
  } catch {
    return false
  }
}

function clearReconnectTimer() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
}
function clearPingTimer() {
  if (pingTimer) {
    clearInterval(pingTimer)
    pingTimer = null
  }
}

function resetState() {
  if (socket) {
    try {
      socket.onopen = null
      socket.onclose = null
      socket.onerror = null
      socket.onmessage = null
      socket.close()
    } catch {}
  }
  socket = null
  isConnected = false
  clearReconnectTimer()
  clearPingTimer()
}

// ==== API pública ===========================================================

export function isReady() {
  return !!socket && isConnected && socket.readyState === WebSocket.OPEN
}

export function disconnectStudent() {
  currentIdentity = null
  resetState()
}

// Conecta (o reconecta) si es necesario.
// identity: { centerId, username, name, lastName }
export function connectStudent(identity) {
  // Guardamos identidad para reconexiones automáticas
  currentIdentity = identity

  // Si ya estamos conectados con el mismo usuario, no hagas nada
  if (isReady()) return

  // Validaciones mínimas
  //if (!identity || identity.centerId !== 9999 || !identity.username) {
  if (!identity || identity.centerId !== 2 || !identity.username) {
    // Guard puede invocar desconexión en el Provider
    disconnectStudent()
    return
  }

  // Arranca la conexión
  openSocket(buildWsUrl(identity))
}

// ==== Core de socket ========================================================

function openSocket(url) {
  resetState() // limpia restos anteriores
  socket = new WebSocket(url)

  socket.onopen = () => {
    isConnected = true
    reconnectAttempts = 0

    // Arranca ping keep-alive
    clearPingTimer()
    pingTimer = setInterval(() => {
      safeSend({ type: 'ping', ts: Date.now() })
    }, PING_INTERVAL_MS)
  }

  socket.onclose = () => {
    isConnected = false
    clearPingTimer()

    // Reintentos de reconexión si aún tenemos identidad válida
    if (!currentIdentity) return
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return

    const backoffMs = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000)
    reconnectAttempts++

    clearReconnectTimer()
    reconnectTimer = setTimeout(() => {
      openSocket(buildWsUrl(currentIdentity))
    }, backoffMs)
  }

  socket.onerror = (e) => {
    // Error de red o CORS/upgrade rechazado, onclose se disparará también
    // No hacemos nada especial aquí
    // console.error('[WS Student] onerror', e)
  }

  socket.onmessage = async (event) => {
    let payload = null
    try {
      payload = JSON.parse(event.data)
    } catch {
      return
    }
    if (!payload || !payload.type) return

    switch (payload.type) {
      case 'pong':
        // opcional: medir RTT
        break

      case 'request-snapshot': {
        // Servidor pide una captura. Debe venir requesterId.
        const requesterId = payload.requesterId
        if (!requesterId) return

        const dataUrl = await captureViewportDataUrl()
        if (!dataUrl) return

        safeSend({
          type: 'snapshot',
          requesterId,
          dataUrl,
          ts: Date.now()
        })
        break
      }

      default:
        // Ignorar otros tipos
        break
    }
  }
}










// // src/bl/WsStudent.js
// // ======================================================================
// // WsStudent (singleton)
// //  - Conecta al WebSocket como Ztudiante *sólo* si center.id === 9999
// //  - Mantiene reconexión básica (hasta 10 intentos, 3s entre intentos)
// //  - Responde automáticamente a "request-snapshot" con el viewport
// //  - Expone helpers: connectStudent, disconnectStudent, isReady, send,
// //                    setMessageHandler, clearMessageHandler
// //  - Carga html2canvas bajo demanda para no penalizar el performance
// // ======================================================================

// let socket = null
// let isConnected = false
// let reconnectTimeout = null
// let reconnectAttempts = 0
// const MAX_RECONNECT_ATTEMPTS = 10

// // Buffer para mensajes entrantes hasta que el handler se registre
// let pendingMessages = []
// let currentMessageHandler = null

// // ---- Utilidades de sesión ---------------------------------------------------

// function readSession() {
//   try {
//     const raw = sessionStorage.getItem('userData')
//     if (!raw) return null
//     const s = JSON.parse(raw)

//     // El usuario mencionó una posible llave mal escrita "navigaton".
//     const nav = s.navigation || s.navigaton || {}

//     const centerId = nav?.center?.id ?? null
//     const userCode = nav?.username ?? null

//     const name = s?.user?.name ?? ''
//     const lastName = s?.user?.lastName ?? ''

//     return {
//       centerId,
//       user: userCode,
//       name,
//       surname: lastName
//     }
//   } catch {
//     return null
//   }
// }

// function canConnectStudent(session) {
//   // Regla: sólo centro 9999 puede usar esta función
//   if (!session?.centerId) return { ok: false, reason: 'missing-centerId' }
//   //if (Number(session.centerId) !== 9999) return { ok: false, reason: 'center-not-allowed' }
//   //if (Number(session.centerId) !== 318) return { ok: false, reason: 'center-not-allowed' }
//   if (Number(session.centerId) !== 2) return { ok: false, reason: 'center-not-allowed' }
//   if (!session?.user) return { ok: false, reason: 'missing-user' }
//   return { ok: true }
// }

// // ---- API pública mínima -----------------------------------------------------

// export function isReady() {
//   return socket && isConnected && socket.readyState === WebSocket.OPEN
// }

// export function send(payload) {
//   try {
//     if (!isReady()) return false
//     socket.send(JSON.stringify(payload))
//     return true
//   } catch {
//     return false
//   }
// }

// export function setMessageHandler(fn) {
//   currentMessageHandler = typeof fn === 'function' ? fn : null

//   // Si ya hay mensajes encolados, drénalos
//   if (currentMessageHandler && pendingMessages.length > 0) {
//     const queue = pendingMessages
//     pendingMessages = []
//     for (const msg of queue) {
//       try { currentMessageHandler(msg) } catch {}
//     }
//   }
// }

// export function clearMessageHandler() {
//   currentMessageHandler = null
// }

// export function disconnectStudent() {
//   try { _resetWsState() } catch {}
// }

// export function connectStudent() {
//   // No dupliques conexiones
//   if (socket || isConnected) return

//   const session = readSession()
//   const check = canConnectStudent(session)
//   if (!check.ok) {
//     console.warn('[WsStudent] No se conectará (motivo):', check.reason)
//     return
//   }

//   const urlWss = (process.env.REACT_APP_API_URL_WSS || 'ws://localhost:5000/ws') +
//     `?centerId=${encodeURIComponent(session.centerId)}&role=student` +
//     `&user=${encodeURIComponent(session.user)}` +
//     `&name=${encodeURIComponent(session.name || '')}` +
//     `&surname=${encodeURIComponent(session.surname || '')}`

//   const connect = () => {
//     if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
//       console.warn('[WsStudent] Límite de reconexiones alcanzado')
//       return
//     }

//     reconnectAttempts++
//     socket = new WebSocket(urlWss)

//     socket.onopen = () => {
//       isConnected = true
//       reconnectAttempts = 0
//       // console.debug('[WsStudent] Conectado')
//     }

//     socket.onclose = () => {
//       isConnected = false
//       socket = null
//       // console.debug('[WsStudent] onclose → reconectar')
//       reconnectTimeout = setTimeout(connect, 3000)
//     }

//     socket.onerror = (e) => {
//       // console.warn('[WsStudent] onerror', e)
//     }

//     socket.onmessage = async (event) => {
//       let payload = null
//       try { payload = JSON.parse(event.data) } catch { /* ignora */ }

//       // Siempre encolamos primero
//       if (payload) pendingMessages.push(payload)

//       // Respuestas automáticas del Ztudiante
//       try {
//         if (payload?.type === 'request-snapshot') {
//           // Estructura esperada desde el backend:
//           // { type: 'request-snapshot', requesterId: 'inst1', capture: 'viewport'|'fullpage'|'element', ts: ... }
//           const requesterId = payload.requesterId
//           const mode = payload.capture || 'viewport'

//           if (requesterId) {
//             const dataUrl = await _captureViewportAsDataUrl(mode)
//             if (dataUrl) {
//               send({
//                 type: 'snapshot',
//                 requesterId,
//                 dataUrl
//               })
//             }
//           }
//         }
//       } catch (e) {
//         console.error('[WsStudent] Error al responder snapshot:', e)
//       }

//       // Si hay handler de UI, drena en orden
//       if (typeof currentMessageHandler === 'function') {
//         const queue = pendingMessages
//         pendingMessages = []
//         for (const msg of queue) {
//           try { currentMessageHandler(msg) } catch {}
//         }
//       }
//     }
//   }

//   connect()
// }

// // ---- Internos ---------------------------------------------------------------

// function _resetWsState() {
//   if (socket) {
//     socket.onopen = null
//     socket.onclose = null
//     socket.onerror = null
//     socket.onmessage = null
//     try { socket.close() } catch {}
//   }
//   socket = null
//   isConnected = false
//   clearTimeout(reconnectTimeout)
//   reconnectAttempts = 0
//   // Nota: no limpiamos pendingMessages para no perder mensajes tardíos si el handler se registra después
// }

// async function _captureViewportAsDataUrl(mode) {
//   // Sólo implementamos viewport (lo que “se ve”). 
//   // Si en el futuro quieres "fullpage", podemos intentar con scrollHeight.
//   try {
//     const html2canvas = (await import('html2canvas')).default

//     if (mode === 'fullpage') {
//       // Intento simple de “full page”: clona canvas con altura del documento completo.
//       // Dependencia: muchos sitios bloquean CORS en imágenes; usa useCORS=true.
//       // Ten en cuenta que "fullpage" puede ser pesado; si ves problemas, mantén 'viewport'.
//       const width = document.documentElement.clientWidth
//       const height = Math.max(
//         document.body.scrollHeight,
//         document.documentElement.scrollHeight,
//         document.body.offsetHeight,
//         document.documentElement.offsetHeight,
//         document.body.clientHeight,
//         document.documentElement.clientHeight
//       )
//       const canvas = await html2canvas(document.body, {
//         useCORS: true,
//         logging: false,
//         windowWidth: width,
//         windowHeight: height
//       })
//       return canvas.toDataURL('image/jpeg', 0.8)
//     }

//     // Por defecto: viewport
//     const canvas = await html2canvas(document.body, {
//       useCORS: true,
//       logging: false
//     })
//     return canvas.toDataURL('image/jpeg', 0.8)
//   } catch (e) {
//     console.error('[WsStudent] html2canvas error:', e)
//     return null
//   }
// }










// // =====================================================
// //  WebSocket del Ztudiante (singleton)
// //  - Conexión (solo si center.id === 9999)
// //  - Reconexión con backoff
// //  - Heartbeat (ping/pong)
// //  - Handler externo (setMessageHandler)
// //  - Atiende "request-snapshot" -> envía snapshot del viewport
// //  - Lee sesión desde sessionStorage (navigation/navigaton)
// //  - URL WSS desde REACT_APP_API_URL_WSS (fallback: ws://localhost:5000/ws)
// // =====================================================

// let socket = null;
// let isConnected = false;
// let reconnectAttempts = 0;
// let reconnectTimer = null;
// let heartbeatTimer = null;

// // Buffer + handler para mensajes que llegan antes de que registres un onMessage
// let pendingMessages = [];
// let currentMessageHandler = null;

// // ---------- Configuración ----------
// const MAX_RECONNECT_ATTEMPTS = 10;
// const RECONNECT_BASE_DELAY_MS = 1200; // exponencial
// const HEARTBEAT_INTERVAL_MS = 30000;  // 30s

// // Lee la sesión y devuelve datos consistentes
// function readSession() {
//   try {
//     const key = process.env.REACT_APP_SESSION || 'SESSION';
//     const raw = sessionStorage.getItem(key);
//     if (!raw) return null;

//     const s = JSON.parse(raw);

//     // Soporta tanto "navigation" como el typo "navigaton"
//     const nav = s.navigation || s.navigaton || {};
//     const center = nav.center || {};
//     const role = nav.role || {};
//     const username = nav.username || nav.user || nav.login || null;

//     const userObj = s.user || {};
//     const name = userObj.name || '';
//     const surname = userObj.lastName || '';

//     return {
//       centerId: center.id ?? null,
//       roleId: role.id ?? null,
//       username,
//       name,
//       surname,
//     };
//   } catch {
//     return null;
//   }
// }

// // Construye la URL de conexión WS
// function buildWsUrl({ centerId, username, name, surname }) {
//   //const base = process.env.REACT_APP_API_URL_WSS || 'ws://localhost:5000/ws';
//   const base = process.env.REACT_APP_API_BASE_WSS;
//   const qs = new URLSearchParams({
//     centerId: String(centerId ?? ''),
//     role: 'student',
//     user: String(username ?? ''),
//     name: String(name ?? ''),
//     surname: String(surname ?? ''),
//   });
//   return `${base}?${qs.toString()}`;
// }

// // ------------- Estado / utilidades -------------
// export function isReady() {
//   return socket && isConnected && socket.readyState === WebSocket.OPEN;
// }

// function resetState() {
//   try {
//     if (socket) {
//       socket.onopen = null;
//       socket.onclose = null;
//       socket.onerror = null;
//       socket.onmessage = null;
//       try { socket.close(); } catch {}
//     }
//   } catch {}
//   socket = null;
//   isConnected = false;
//   clearTimeout(reconnectTimer);
//   reconnectTimer = null;
//   clearInterval(heartbeatTimer);
//   heartbeatTimer = null;
//   // Nota: no vaciamos pendingMessages para no perder mensajes tardíos.
// }

// // ------------- Heartbeat -------------
// function startHeartbeat() {
//   clearInterval(heartbeatTimer);
//   heartbeatTimer = setInterval(() => {
//     try {
//       if (isReady()) {
//         socket.send(JSON.stringify({ type: 'ping', ts: Date.now() }));
//       }
//     } catch {}
//   }, HEARTBEAT_INTERVAL_MS);
// }

// // ------------- Manejo de mensajes -------------
// function installOnMessage() {
//   socket.onmessage = async (event) => {
//     let payload = null;
//     try {
//       payload = JSON.parse(event.data);
//     } catch {
//       // Mensaje no JSON, ignorar
//       return;
//     }

//     // Responder ping del servidor (por si llega)
//     if (payload?.type === 'ping') {
//       try {
//         if (isReady()) {
//           socket.send(JSON.stringify({ type: 'pong', ts: Date.now() }));
//         }
//       } catch {}
//       // y continuamos para propagarlo si alguien quiere verlo
//     }

//     // Atender solicitud de snapshot
//     if (payload?.type === 'request-snapshot') {
//       const requesterId = payload.requesterId;
//       const capture = payload.capture || 'viewport'; // por ahora viewport

//       if (requesterId && isReady()) {
//         try {
//           const dataUrl = await captureViewport(); // <-- viewport por requerimiento actual
//           const response = {
//             type: 'snapshot',
//             requesterId,
//             dataUrl,
//             ts: Date.now(),
//           };
//             socket.send(JSON.stringify(response));
//         } catch (err) {
//           // Si falla la captura, opcionalmente notificar un error al server
//           try {
//             socket.send(JSON.stringify({
//               type: 'error',
//               code: 'snapshot-failed',
//               message: (err && err.message) || 'snapshot error'
//             }));
//           } catch {}
//         }
//       }
//       // No devolvemos aquí; también encolamos el mensaje para que el UI pueda reaccionar si quiere.
//     }

//     // Encolar siempre y drenar si ya hay handler
//     pendingMessages.push(payload);
//     if (typeof currentMessageHandler === 'function') {
//       const queue = pendingMessages;
//       pendingMessages = [];
//       for (const msg of queue) {
//         try { currentMessageHandler(msg); } catch (e) { console.error('[WS Student] handler error:', e); }
//       }
//     }
//   };
// }

// // ------------- Conexión / Reconexión -------------
// function scheduleReconnect(connectFn) {
//   if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
//     console.warn('[WS Student] Límite de reconexiones alcanzado.');
//     return;
//   }
//   const delay = RECONNECT_BASE_DELAY_MS * Math.pow(1.6, reconnectAttempts);
//   reconnectAttempts++;
//   clearTimeout(reconnectTimer);
//   reconnectTimer = setTimeout(connectFn, delay);
// }

// export function connectStudent() {
//   // 1) Leer sesión
//   const session = readSession();
//   if (!session) {
//     console.warn('[WS Student] No hay sesión válida en sessionStorage.');
//     return;
//   }

//   const { centerId, username, name, surname } = session;

//   // 2) Regla: solo centro 9999 debe conectar
//   if (String(centerId) !== '9999') {
//     console.info(`[WS Student] Centro ${centerId} no autorizado para WS (solo 9999). No se conectará.`);
//     return;
//   }

//   // 3) Validaciones mínimas
//   if (!username) {
//     console.warn('[WS Student] No hay "username" en la sesión, no se puede abrir WS.');
//     return;
//   }

//   if (socket || isConnected) {
//     // ya hay socket en progreso o conectado
//     return;
//   }

//   const url = buildWsUrl({ centerId, username, name, surname });

//   const doConnect = () => {
//     try {
//       socket = new WebSocket(url);

//       socket.onopen = () => {
//         isConnected = true;
//         reconnectAttempts = 0;
//         startHeartbeat();
//         // Drenar mensajes pendientes si ya hubiera handler
//         if (typeof currentMessageHandler === 'function' && pendingMessages.length > 0) {
//           const queue = pendingMessages;
//           pendingMessages = [];
//           for (const msg of queue) {
//             try { currentMessageHandler(msg); } catch (e) { console.error('[WS Student] handler error:', e); }
//           }
//         }
//         // console.log('[WS Student] Conectado.');
//       };

//       socket.onclose = () => {
//         isConnected = false;
//         // console.log('[WS Student] Cerrado. Reintentando…');
//         scheduleReconnect(doConnect);
//       };

//       socket.onerror = () => {
//         // console.log('[WS Student] Error. Reintentando…');
//         try { socket.close(); } catch {}
//         isConnected = false;
//         scheduleReconnect(doConnect);
//       };

//       installOnMessage();
//     } catch (err) {
//       scheduleReconnect(doConnect);
//     }
//   };

//   doConnect();
// }

// export function disconnectStudent() {
//   resetState();
// }

// export function setMessageHandler(fn) {
//   currentMessageHandler = (typeof fn === 'function') ? fn : null;

//   // Si ya hay mensajes pendientes, drénalos
//   if (typeof currentMessageHandler === 'function' && pendingMessages.length > 0) {
//     const queue = pendingMessages;
//     pendingMessages = [];
//     for (const msg of queue) {
//       try { currentMessageHandler(msg); } catch (e) { console.error('[WS Student] handler error:', e); }
//     }
//   }
// }

// export function clearMessageHandler() {
//   currentMessageHandler = null;
// }

// export function send(data) {
//   try {
//     if (!isReady()) return false;
//     socket.send(JSON.stringify(data));
//     return true;
//   } catch {
//     return false;
//   }
// }

// // =====================================================
// //  Captura del viewport (simple) con import dinámico
// //  - No requiere instalar html2canvas hasta que lo uses
// //  - Evita "brincos": no toca el scroll del usuario
// // =====================================================
// async function captureViewport() {
//   // Carga perezosa para no inflar el bundle si nunca se usa
//   const html2canvas = (await import('html2canvas')).default;

//   // Captura del viewport: renderiza lo visible (window)
//   const canvas = await html2canvas(document.body, {
//     useCORS: true,
//     logging: false,
//     backgroundColor: '#ffffff'
//   });

//   // Ajusta la calidad si deseas (0.92 es default)
//   const dataUrl = canvas.toDataURL('image/png', 0.92);
//   return dataUrl;
// }